import {
    offer_visit_count,
    selected_types_from_offer
} from '../../../../helpers/offerObject';
import { checkPermissions } from '../../../../helpers/checkPermissions';

export const can_view_offer_preview_internal_ui = (roles = []) => {
    return checkPermissions('Scout', roles);
};

export const can_mark_agency_offer_paid = (roles = []) => {
    return checkPermissions('Knight', roles);
};

export const initial_offer_payment_recording = {
    registerPrepayment: false,
    partialAmount: ''
};

export const offer_payment_recording_reducer = (state, action = {}) => {
    switch (action.type) {
    case 'toggle-register-prepayment':
        return {
            ...state,
            registerPrepayment: !state.registerPrepayment,
            partialAmount: state.registerPrepayment ? '' : state.partialAmount
        };
    case 'change-prepayment':
        return {
            ...state,
            partialAmount: action.value
        };
    case 'reset':
        return { ...initial_offer_payment_recording };
    default:
        return state;
    }
};

export const build_payment_confirmation = ({
    registerPrepayment = false,
    partialAmount = '',
    fulfillRemainingPayment = false
} = {}) => {
    if (fulfillRemainingPayment) {
        return {
            fulfillRemainingPayment: true
        };
    }

    return {
        includeInGoal: true,
        goalTreatment: registerPrepayment ? 'partial_payment' : 'include_goal',
        ...(registerPrepayment ? { partialAmount: Number(partialAmount) } : {})
    };
};

export const build_payment_overview = (offerDraft) => {
    const priceData = offerDraft?.priceData || {};
    const recurringPaymentLinkUrl = `${priceData?.recurringPaymentLinkUrl || priceData?.paymentLinkUrl || ''}`.trim();
    const oneOffPaymentLinkUrl = `${priceData?.oneOffPaymentLinkUrl || ''}`.trim();
    const recurringPaymentMethod = `${priceData?.recurringPaymentMethod || ''}`.trim().toLowerCase();
    const recurringPaymentUsesBankTransfer = priceData?.recurringPaymentUsesBankTransfer === true || recurringPaymentMethod === 'bank_transfer';
    const oneOffPaymentMethod = `${priceData?.oneOffPaymentMethod || ''}`.trim().toLowerCase();
    const oneOffPaymentUsesBankTransfer = priceData?.oneOffPaymentUsesBankTransfer === true || oneOffPaymentMethod === 'bank_transfer';
    const hasRecurring = priceData?.hasRecurringServices === true || recurringPaymentLinkUrl.length > 0 || recurringPaymentUsesBankTransfer;
    const hasOneOff = priceData?.hasOneOffServices === true
        || oneOffPaymentLinkUrl.length > 0
        || oneOffPaymentUsesBankTransfer
        || oneOffPaymentMethod === 'payment_link';
    const fallbackRecurring = !hasRecurring && !hasOneOff;

    return {
        hasRecurring: hasRecurring || fallbackRecurring,
        hasOneOff,
        recurringPaymentLinkUrl,
        oneOffPaymentLinkUrl,
        recurringUsesBankTransfer: recurringPaymentUsesBankTransfer || (hasRecurring && !recurringPaymentLinkUrl.length),
        oneOffUsesBankTransfer: oneOffPaymentUsesBankTransfer || (hasOneOff && !oneOffPaymentLinkUrl.length)
    };
};

export const read_route_language = (language = '') => {
    const normalizedLanguage = `${language || ''}`.trim().toLowerCase();

    if (normalizedLanguage === 'en' || normalizedLanguage.startsWith('en-') || normalizedLanguage.startsWith('en_')) {
        return 'en';
    }

    return 'el';
};

export const offer_is_invalid = (offerDraft) => {
    return `${offerDraft?.status || ''}`.trim().toLowerCase() === 'invalid';
};

export const offer_can_extend = (offerDraft, now = new Date()) => {
    const normalizedStatus = `${offerDraft?.status || ''}`.trim().toLowerCase();

    if (normalizedStatus === 'invalid' || normalizedStatus === 'expired') {
        return true;
    }

    if (!['sent', 'valid', 'ready_for_view'].includes(normalizedStatus)) {
        return false;
    }

    const createdAt = new Date(offerDraft?.createdAt || offerDraft?.updatedAt || '');

    if (!Number.isFinite(createdAt.getTime())) {
        return false;
    }

    const validForDays = Number(offerDraft?.priceData?.validFor ?? 30) || 30;
    const expiryDate = new Date(createdAt);

    expiryDate.setDate(expiryDate.getDate() + validForDays);

    return new Date(now).getTime() > expiryDate.getTime();
};

export const offer_is_unavailable = (offerDraft) => {
    const normalizedStatus = `${offerDraft?.status || ''}`.trim().toLowerCase();
    const createdAt = new Date(offerDraft?.createdAt || offerDraft?.updatedAt || Date.now());
    const validForDays = Number(offerDraft?.priceData?.validFor ?? 30) || 30;
    const expiryDate = new Date(createdAt);

    expiryDate.setDate(expiryDate.getDate() + validForDays);

    if (normalizedStatus === 'invalid' || normalizedStatus === 'expired') {
        return true;
    }

    return new Date() > expiryDate;
};

export const offerPreviewCopyByLanguage = {
    el: {
        acceptanceTitle: 'Αποδοχή όρων προσφοράς',
        acceptancePending: 'Εκκρεμεί αποδοχή',
        acceptanceAccepted: 'Έγινε αποδοχή',
        acceptanceText: 'Δηλώνω υπεύθυνα ότι έχω μελετήσει πλήρως το περιεχόμενο, τους όρους και τις οικονομικές διατάξεις της παρούσας προσφοράς και τους αποδέχομαι ανεπιφύλακτα στο σύνολό τους.',
        processing: 'Επεξεργασία...',
        proceedRecurringPayment: 'Συνέχεια σε πληρωμή συνδρομής',
        proceedOneOffPayment: 'Συνέχεια με τραπεζική κατάθεση',
        proceedBankTransfer: 'Συνέχεια με τραπεζική κατάθεση',
        unavailableTitle: 'Η προσφορά δεν είναι πλέον διαθέσιμη',
        unavailableText: 'Η προσφορά δεν είναι πλέον διαθέσιμη. Παρακαλώ επικοινωνήστε στο support@doitforme.eu',
        bankTransferDetails: 'Στοιχεία τραπεζικής κατάθεσης',
        bankTransferText: 'Χρησιμοποιήστε τα παρακάτω στοιχεία για να ολοκληρώσετε την πληρωμή.',
        beneficiary: 'Δικαιούχος',
        reference: 'Αιτιολογία',
        amount: 'Ποσό',
        close: 'Κλείσιμο',
        exportPdf: 'Εξαγωγή PDF',
        extendDays: 'Ημέρες',
        extend: 'Παράταση',
        extending: 'Παράταση...',
        invalidExtendDays: 'Συμπληρώστε έγκυρες ημέρες παράτασης.',
        extendOfferFailed: 'Δεν ήταν δυνατή η παράταση της προσφοράς.',
        paid: 'Πληρώθηκε',
        fulfillPayment: 'Εξόφληση πληρωμής',
        fulfillPaymentTitle: 'Εξόφληση υπολοίπου',
        fulfillPaymentAction: 'Εξόφληση πληρωμής',
        fulfillmentGoalIncluded: 'Το υπόλοιπο θα συμπεριληφθεί στον στόχο.',
        paidSliderTitle: 'Καταχώριση πληρωμής',
        paidSliderAction: 'Καταχώριση ως πληρωμένη',
        partialSliderAction: 'Καταχώριση μερικής πληρωμής',
        registerPrepayment: 'Καταχώριση προκαταβολής',
        prepaymentAmount: 'Προκαταβολή',
        paymentTotal: 'Συνολικό ποσό',
        paymentRemaining: 'Υπόλοιπο',
        partialAmountRequired: 'Η προκαταβολή πρέπει να είναι μεγαλύτερη από μηδέν και μικρότερη από το συνολικό ποσό.',
        delete: 'Διαγραφή',
        deleting: 'Διαγραφή...',
        deleteOfferQuestion: 'Διαγραφή προσφοράς;',
        yes: 'Ναι',
        no: 'Όχι',
        loadingOffer: 'Φόρτωση προσφοράς...',
        offerNotFound: 'Η προσφορά δεν βρέθηκε.',
        paymentLinkMissing: 'Δεν έχει ρυθμιστεί ακόμη payment link για αυτή την προσφορά.',
        paymentStartFailed: 'Δεν ήταν δυνατή η έναρξη της πληρωμής.',
        paidStatusFailed: 'Δεν ήταν δυνατή η αλλαγή κατάστασης σε Paid.',
        cerebroLinked: 'Linked · Tasks',
        cerebroSyncPending: 'Η πληρωμή αποθηκεύτηκε · Η σύνδεση με Tasks εκκρεμεί',
        cerebroSyncFailed: 'Η πληρωμή αποθηκεύτηκε · Η σύνδεση με Tasks χρειάζεται επανάληψη',
        deleteOfferFailed: 'Δεν ήταν δυνατή η διαγραφή της προσφοράς.'
    },
    en: {
        acceptanceTitle: 'Offer terms acceptance',
        acceptancePending: 'Pending acceptance',
        acceptanceAccepted: 'Accepted',
        acceptanceText: 'I formally confirm that I have fully reviewed the content, terms and financial provisions of this offer and I accept them in full without reservation.',
        processing: 'Processing...',
        proceedRecurringPayment: 'Proceed recurring payment',
        proceedOneOffPayment: 'Proceed with bank transfer',
        proceedBankTransfer: 'Proceed with bank transfer',
        unavailableTitle: 'This offer is no longer available',
        unavailableText: 'This offer is no longer available. Please contact support@doitforme.eu',
        bankTransferDetails: 'Bank transfer details',
        bankTransferText: 'Use the following details to complete the payment.',
        beneficiary: 'Beneficiary',
        reference: 'Reference',
        amount: 'Amount',
        close: 'Close',
        exportPdf: 'Export PDF',
        extendDays: 'Days',
        extend: 'Extend',
        extending: 'Extending...',
        invalidExtendDays: 'Enter valid extension days.',
        extendOfferFailed: 'Could not extend this offer.',
        paid: 'Paid',
        fulfillPayment: 'Fulfill payment',
        fulfillPaymentTitle: 'Fulfill remaining balance',
        fulfillPaymentAction: 'Fulfill payment',
        fulfillmentGoalIncluded: 'The remaining balance will be included in the goal.',
        paidSliderTitle: 'Record payment',
        paidSliderAction: 'Mark as paid',
        partialSliderAction: 'Record partial payment',
        registerPrepayment: 'Register prepayment',
        prepaymentAmount: 'Prepayment',
        paymentTotal: 'Total amount',
        paymentRemaining: 'Left to pay',
        partialAmountRequired: 'The prepayment must be greater than zero and lower than the total amount.',
        delete: 'Delete',
        deleting: 'Deleting...',
        deleteOfferQuestion: 'Delete this offer?',
        yes: 'Yes',
        no: 'No',
        loadingOffer: 'Loading offer...',
        offerNotFound: 'Offer not found.',
        paymentLinkMissing: 'Payment link is not configured for this offer yet.',
        paymentStartFailed: 'Could not start payment.',
        paidStatusFailed: 'Could not change this offer to Paid.',
        cerebroLinked: 'Linked · Tasks',
        cerebroSyncPending: 'Payment saved · Tasks link pending',
        cerebroSyncFailed: 'Payment saved · Tasks link needs retry',
        deleteOfferFailed: 'Could not delete this offer.'
    }
};

export const payment_state_by_type = (offerDraft, paymentType) => {
    const paymentState = offerDraft?.paymentState || {};
    const payments = offerDraft?.payments || {};

    if (paymentType === 'recurring') {
        return `${paymentState?.recurring || payments?.recurring?.status || ''}`.trim().toLowerCase();
    }

    if (paymentType === 'one_off') {
        return `${paymentState?.oneOff || paymentState?.one_off || payments?.oneOff?.status || ''}`.trim().toLowerCase();
    }

    return '';
};

export const payment_recording_context_from_offer = (offerDraft = {}, paymentType = '') => {
    const paymentKey = paymentType === 'one_off' ? 'oneOff' : 'recurring';
    const paymentData = offerDraft?.payments?.[paymentKey] || {};
    const status = `${paymentData?.status || payment_state_by_type(offerDraft, paymentType)}`.trim().toLowerCase();
    const remainingAmount = Number(paymentData?.remainingAmount || 0);
    const totalAmount = Number(paymentData?.totalAmount || 0);

    return {
        fulfillRemainingPayment: ['partial', 'partially_paid'].includes(status),
        remainingAmount: Number.isFinite(remainingAmount) ? remainingAmount : 0,
        totalAmount: Number.isFinite(totalAmount) ? totalAmount : 0
    };
};

export const total_amount_by_payment_type = (offerDraft, paymentType) => {
    const priceData = offerDraft?.priceData || {};
    const total = Number(priceData?.total || 0);
    const recurringTotalWithVat = Number(
        priceData?.mixedRecurringManualTotal
        || priceData?.recurringTotalWithVat
        || priceData?.mixedRecurringTotalValue
        || priceData?.calculatedRecurringTotalWithVat
        || 0
    );
    const oneOffTotalWithVat = Number(
        priceData?.mixedOneOffManualTotal
        || priceData?.oneOffTotalWithVat
        || priceData?.mixedOneOffTotalValue
        || priceData?.calculatedOneOffTotalWithVat
        || 0
    );
    const recurringIsFinite = Number.isFinite(recurringTotalWithVat);
    const oneOffIsFinite = Number.isFinite(oneOffTotalWithVat);
    const totalIsFinite = Number.isFinite(total);

    if (paymentType === 'recurring') {
        if (recurringIsFinite && recurringTotalWithVat > 0) {
            return recurringTotalWithVat;
        }

        return totalIsFinite ? total : 0;
    }

    if (paymentType === 'one_off') {
        if (oneOffIsFinite && oneOffTotalWithVat > 0) {
            return oneOffTotalWithVat;
        }

        return totalIsFinite ? total : 0;
    }

    return totalIsFinite ? total : 0;
};

const offerVisitorCookieName = 'godfather_offer_visitor_id';

const read_cookie_value = (cookieName = '') => {
    const cookies = `${document.cookie || ''}`.split(';');
    const match = cookies
        .map((cookieItem) => cookieItem.trim())
        .find((cookieItem) => cookieItem.startsWith(`${cookieName}=`));

    if (!match) {
        return '';
    }

    return decodeURIComponent(match.slice(cookieName.length + 1));
};

const write_cookie_value = (cookieName, cookieValue) => {
    document.cookie = `${cookieName}=${encodeURIComponent(cookieValue)}; path=/; max-age=31536000; SameSite=Lax`;
};

const build_offer_visitor_id = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).slice(2, 10);

    return `visitor_${timestamp}_${random}`;
};

export const read_or_create_visitor_id = () => {
    const existingVisitorId = read_cookie_value(offerVisitorCookieName);

    if (existingVisitorId.length) {
        return existingVisitorId;
    }

    const nextVisitorId = build_offer_visitor_id();
    write_cookie_value(offerVisitorCookieName, nextVisitorId);
    return nextVisitorId;
};

const visit_session_key = (offerId = '') => {
    return `godfather_offer_visit_session_${offerId}`;
};

export const visit_already_recorded_for_session = (offerId = '') => {
    return `${window.sessionStorage.getItem(visit_session_key(offerId)) || ''}`.length > 0;
};

export const mark_visit_recorded_for_session = (offerId = '') => {
    window.sessionStorage.setItem(visit_session_key(offerId), new Date().toISOString());
};

export const read_offer_total_visits = (offerDraft = {}) => {
    return offer_visit_count(offerDraft);
};

export const initialised_payment_types_from_offer = (offerDraft = {}) => {
    const recurringPaymentState = payment_state_by_type(offerDraft, 'recurring');
    const oneOffPaymentState = payment_state_by_type(offerDraft, 'one_off');
    const initialisedPaymentTypes = [];

    if (['initialised', 'partial', 'partially_paid'].includes(recurringPaymentState)) {
        initialisedPaymentTypes.push('recurring');
    }

    if (['initialised', 'partial', 'partially_paid'].includes(oneOffPaymentState)) {
        initialisedPaymentTypes.push('one_off');
    }

    if (initialisedPaymentTypes.length) {
        return initialisedPaymentTypes;
    }

    const normalizedStatus = `${offerDraft?.status || ''}`.trim().toLowerCase();
    const normalizedPaymentStatus = `${offerDraft?.paymentStatus || ''}`.trim().toLowerCase();
    const paymentOverview = build_payment_overview(offerDraft);
    const legacyInitialised = normalizedStatus === 'paid_initialised'
        || normalizedStatus === 'payment_initialised'
        || normalizedPaymentStatus === 'initialised';

    if (legacyInitialised && paymentOverview.hasRecurring) {
        return ['recurring'];
    }

    if (legacyInitialised && paymentOverview.hasOneOff) {
        return ['one_off'];
    }

    return initialisedPaymentTypes;
};

export const read_cerebro_connection_message = (offerDraft = {}, copy = {}) => {
    const connection = offerDraft?.workflow?.cerebroConnection || {};
    const status = connection?.clientId ? 'linked' : `${connection?.status || ''}`.trim().toLowerCase();
    const messages = {
        linked: copy.cerebroLinked,
        synced: copy.cerebroLinked,
        pending: copy.cerebroSyncPending,
        resolving: copy.cerebroSyncPending,
        failed: copy.cerebroSyncFailed
    };

    return messages[status] || '';
};
